import React, {Fragment, forwardRef, useEffect, useRef, useImperativeHandle, useMemo} from 'react'
import {Group, Image, Rect, Transformer} from 'react-konva'
import Konva from 'konva'
import useImage from 'use-image'
import {debounce} from 'lodash'
import useEditorSafeZone from '../../../../hooks/useEditorSafeZone'
import useImagePadding from '../../../../hooks/useImagePadding'
import SpinnerLayer from './SpinnerLayer'
import {useFragmentDetection} from '../../../custom-shape/ui/hooks/useFragmentDetection'

// TODO: fix change color background
// TODO: fix change mockup side

const SafeZoneLayer = forwardRef(({onClick}, ref) => {
    const groupRef = useRef(null)
    const imageRef = useRef(null)
    /**
     * TODO: Prepare for implementing the Safe Zone transform feature
     */
    const trRef = useRef(null)

    useImperativeHandle(ref, () => imageRef.current)

    const {background, safeZone} = useEditorSafeZone()
    const {src, x, y, width, height, fill: _fill, ...backgroundData} = background
    const {src: safeZoneSrc, scaleX = 1, scaleY = 1} = safeZone || {}

    /**
     * Padding background
     */
    const dimensions = useImagePadding({x, y, width, height}, {padding: 4})

    /**
     * Background
     */
    const [backgroundImage, backgroundStatus] = useImage(src, 'anonymous')
    const isBackgroundReady = backgroundStatus === 'loaded'

    /**
     * Safe zone
     */
    const [safeZoneImage, safeZoneStatus] = useImage(safeZoneSrc, 'anonymous')
    const isSafeZoneLoading = safeZoneStatus === 'loading'
    const isSafeZoneReady = safeZoneStatus === 'loaded'

    /**
     * @type {Object}
     * @property {number} [minRegionSize]
     * @property {number} [minWarningSize]
     * @property {number} [minThickness]
     * @property {number} [alphaThreshold]
     * @property {number} [maxCanvasSize]
     * @property {'auto' | 'js' | 'opencv'} [method='auto']
     * @property {number} [dpi]
     * @property {boolean} [enableDetection]
     */
    const fragmentDetectionOptions = {
        enableDetection: true,
    }
    const {fragments, runDetection: _runDetection, isDetecting} = useFragmentDetection(fragmentDetectionOptions)

    const runDetectionRef = useRef(null)
    useEffect(() => {
        if (!runDetectionRef.current) {
            runDetectionRef.current = debounce((image) => {
                if (!image) return

                const shape = new Konva.Image({image})
                _runDetection(shape.toCanvas({pixelRatio: 1}))
            }, 500)
        }

        if (safeZoneImage) {
            runDetectionRef.current(safeZoneImage)
        }

        return () => {
            runDetectionRef.current?.cancel()
            runDetectionRef.current = null
        }
    }, [_runDetection, safeZoneImage])

    const isInvalidSafeZone = useMemo(
        () => fragments.some((frag) => frag && frag.hasThinRegion && frag.minThicknessDetected),
        [fragments],
    )

    const shouldRenderSafeZone = isSafeZoneReady && safeZoneImage && !isInvalidSafeZone

    const imageConfig = useMemo(() => {
        if (isSafeZoneReady && safeZoneImage && !isInvalidSafeZone) {
            return {
                name: 'safeZoneCutout',
                image: safeZoneImage,
                globalCompositeOperation: 'destination-out',
                x: dimensions.width / 2 - (safeZoneImage.width * scaleX) / 2,
                y: dimensions.height / 2 - (safeZoneImage.height * scaleY) / 2,
                width: safeZoneImage.width,
                height: safeZoneImage.height,
                scaleX,
                scaleY,
                listening: false,
                // draggable: true,
            }
        }
        if (isBackgroundReady && backgroundImage) {
            return {
                name: 'safeZone',
                image: backgroundImage,
                globalCompositeOperation: 'source-over',
                x: 0,
                y: 0,
                width: dimensions.width,
                height: dimensions.height,
                listening: false,
                // draggable: false,
            }
        }
        return null
    }, [
        isBackgroundReady,
        backgroundImage,
        isSafeZoneReady,
        safeZoneImage,
        dimensions.width,
        dimensions.height,
        isInvalidSafeZone,
    ])

    useEffect(() => {
        if (!groupRef.current) {
            return
        }
        groupRef.current.cache({pixelRatio: 1})
        groupRef.current.getLayer()?.batchDraw()
    }, [imageConfig])

    // useEffect(() => {
    //     if (!trRef.current || !imageRef.current) {
    //         return
    //     }
    //     trRef.current.nodes([imageRef.current])
    //     trRef.current.getLayer()?.batchDraw()
    // }, [imageConfig])

    // const handleTransform = () => {
    //     if (groupRef.current) {
    //         groupRef.current.cache({pixelRatio: 1})
    //     }
    // }

    return (
        <Fragment>
            <Group name="background" ref={groupRef} x={dimensions.x} y={dimensions.y} onClick={onClick}>
                {shouldRenderSafeZone && (
                    <Rect
                        name="safeZone"
                        listening={false}
                        fill="#f0f0f0"
                        opacity={0.75}
                        width={dimensions.width}
                        height={dimensions.height}
                    />
                )}
                {imageConfig && (
                    <Image
                        {...backgroundData}
                        {...imageConfig}
                        ref={imageRef}
                        rotateEnabled={false}
                        // onTransform={handleTransform}
                        // onDragMove={handleTransform}
                    />
                )}
            </Group>
            {/* TODO: Prepare for implementing the Safe Zone transform feature */}
            {shouldRenderSafeZone && (
                <Transformer
                    ref={trRef}
                    enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
                    // boundBoxFunc={(oldBox, newBox) => {
                    //     if (Math.abs(newBox.width) < 10 || Math.abs(newBox.height) < 10) {
                    //         return oldBox
                    //     }
                    //     return newBox
                    // }}
                />
            )}
        </Fragment>
    )
})

export default SafeZoneLayer
